import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

// Item structure matching API requirements
export interface CategoryItem {
  category_id: string; // UUID from backend
  category_name: string;
  pieces?: number;
  pages?: number;
  weight?: number;
  weightUnit?: 'kg' | 'lbs';
  price?: number;
  image_ids?: string[]; // Changed to string[] to match new API
  // For file management
  files?: File[];
  file_metadata?: Array<{
    name: string;
    size: number;
    type: string;
  }>;
}

export interface ShippingDetailsData {
  departure_airport_id?: number;
  departureAirportName?: string;
  departure_country_name?: string; // Country name from departure airport for category filtering
  departure_country_code?: string;
  destination_airport_id?: number;
  destinationAirportName?: string;
  destination_country_name?: string;
  destination_country_code?: string;
  corridor_id?: string;
  shipping_start_date?: string;
  shipping_end_date?: string;
  selectedCategories: string[]; // Array of category UUIDs
  categoryItems: CategoryItem[]; // Array of items per category (for images only)
  // Simplified single weight/price/description fields
  total_weight?: number; // Weight in lbs
  total_weight_unit?: 'kg' | 'lbs'; // Display unit last selected for the weight field
  total_price?: number; // Price in USD
  description?: string; // Item description
}

export interface ItemPicturesData {
  _deprecated?: boolean;
}

// Step 3: Receiver Details
export interface ReceiverDetailsData {
  name?: string;
  email?: string;
  countryCode?: string;
  phoneNumber?: string;
}

// Complete form data interface
export interface ShipmentFormData {
  shippingDetails: ShippingDetailsData;
  itemPictures: ItemPicturesData;
  receiverDetails: ReceiverDetailsData;
  itemName?: string;
}

interface ShipmentStore {
  currentStep: number;
  formData: ShipmentFormData;

  // Navigation actions
  setCurrentStep: (step: number) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;

  // Data update actions
  updateShippingDetails: (data: Partial<ShippingDetailsData>) => void;
  updateCategoryItem: (categoryId: string, data: Partial<CategoryItem>) => void;
  addCategory: (categoryId: string, categoryName: string) => void;
  removeCategory: (categoryId: string) => void;
  updateItemPictures: (data: Partial<ItemPicturesData>) => void;
  updateReceiverDetails: (data: Partial<ReceiverDetailsData>) => void;
  updateItemName: (itemName: string) => void;

  // Helper methods
  getCategoryItem: (categoryId: string) => CategoryItem | undefined;
  getAllCategoryItems: () => CategoryItem[];

  // Legacy update method for backward compatibility
  updateFormData: (data: unknown) => void;

  // Reset actions
  resetForm: () => void;
  resetStep: (step: 1 | 2 | 3) => void;
}

const initialFormData: ShipmentFormData = {
  shippingDetails: {
    selectedCategories: [],
    categoryItems: [],
    total_weight: undefined,
    total_price: undefined,
    description: undefined,
    departure_country_name: undefined,
  },
  itemPictures: {},
  receiverDetails: {},
};

export const useShipmentStore = create<ShipmentStore>()(
  devtools(
    (set, get) => ({
      currentStep: 1,
      formData: initialFormData,

      setCurrentStep: (step) => set({ currentStep: step }),

      goToNextStep: () =>
        set((state) => ({
          currentStep: Math.min(state.currentStep + 1, 4),
        })),

      goToPreviousStep: () =>
        set((state) => ({
          currentStep: Math.max(state.currentStep - 1, 1),
        })),

      updateShippingDetails: (data) =>
        set((state) => ({
          formData: {
            ...state.formData,
            shippingDetails: { 
              ...state.formData.shippingDetails, 
              ...data,
              // Ensure categoryItems and selectedCategories are preserved
              categoryItems: data.categoryItems ?? state.formData.shippingDetails.categoryItems,
              selectedCategories: data.selectedCategories ?? state.formData.shippingDetails.selectedCategories,
            },
          },
        })),

      addCategory: (categoryId, categoryName) =>
        set((state) => {
          const exists = state.formData.shippingDetails.categoryItems.some(
            item => item.category_id === categoryId
          );

          if (exists) return state;

          return {
            formData: {
              ...state.formData,
              shippingDetails: {
                ...state.formData.shippingDetails,
                selectedCategories: [...state.formData.shippingDetails.selectedCategories, categoryId],
                categoryItems: [
                  ...state.formData.shippingDetails.categoryItems,
                  {
                    category_id: categoryId,
                    category_name: categoryName,
                  },
                ],
              },
            },
          };
        }),

      removeCategory: (categoryId) =>
        set((state) => ({
          formData: {
            ...state.formData,
            shippingDetails: {
              ...state.formData.shippingDetails,
              selectedCategories: state.formData.shippingDetails.selectedCategories.filter(
                id => id !== categoryId
              ),
              categoryItems: state.formData.shippingDetails.categoryItems.filter(
                item => item.category_id !== categoryId
              ),
            },
          },
        })),

      updateCategoryItem: (categoryId, data) =>
        set((state) => {
          const categoryItems = state.formData.shippingDetails.categoryItems.map(item =>
            item.category_id === categoryId
              ? { ...item, ...data }
              : item
          );

          return {
            formData: {
              ...state.formData,
              shippingDetails: {
                ...state.formData.shippingDetails,
                categoryItems,
              },
            },
          };
        }),

      getCategoryItem: (categoryId) => {
        const state = get();
        return state.formData.shippingDetails.categoryItems.find(
          item => item.category_id === categoryId
        );
      },

      getAllCategoryItems: () => {
        const state = get();
        return state.formData.shippingDetails.categoryItems;
      },

      updateItemPictures: (data) =>
        set((state) => ({
          formData: {
            ...state.formData,
            itemPictures: { ...state.formData.itemPictures, ...data },
          },
        })),

      updateReceiverDetails: (data) =>
        set((state) => ({
          formData: {
            ...state.formData,
            receiverDetails: { ...state.formData.receiverDetails, ...data },
          },
        })),

      updateItemName: (itemName) =>
        set((state) => ({
          formData: {
            ...state.formData,
            itemName,
          },
        })),

      // Legacy method - kept for compatibility
      updateFormData: (data) =>
        set((state) => {
          const d = data as {
            departure_airport_id?: number;
            destination_airport_id?: number;
            shipping_start_date?: string;
            shipping_end_date?: string;
            receiver?: { name?: string; email?: string; contact_number?: string };
          };
          const newFormData = { ...state.formData };

          if (d.departure_airport_id !== undefined ||
              d.destination_airport_id !== undefined ||
              d.shipping_start_date !== undefined ||
              d.shipping_end_date !== undefined) {
            newFormData.shippingDetails = {
              ...newFormData.shippingDetails,
              departure_airport_id: d.departure_airport_id,
              destination_airport_id: d.destination_airport_id,
              shipping_start_date: d.shipping_start_date,
              shipping_end_date: d.shipping_end_date,
            };
          }

          if (d.receiver !== undefined) {
            newFormData.receiverDetails = {
              name: d.receiver.name,
              email: d.receiver.email,
              countryCode: d.receiver.contact_number?.substring(0, 2),
              phoneNumber: d.receiver.contact_number?.substring(2),
            };
          }

          return { formData: newFormData };
        }),

      resetForm: () => set({ currentStep: 1, formData: initialFormData }),

      resetStep: (step) =>
        set((state) => {
          const newFormData = { ...state.formData };
          if (step === 1) {
            newFormData.shippingDetails = {
              selectedCategories: [],
              categoryItems: [],
              total_weight: undefined,
              total_price: undefined,
              description: undefined,
              departure_country_name: undefined,
            };
          }
          if (step === 2) newFormData.itemPictures = {};
          if (step === 3) newFormData.receiverDetails = {};
          return { formData: newFormData };
        }),
    }),
    { name: 'ShipmentStore' }
  )
);